/**
 * 客服端会话管理 API 接口
 * 包含：状态管理、会话列表、消息发送/接收、已读标记、数量统计
 */
import { csHttp } from '@/utils/http';
import { API_BASE_URL } from '@/config/env';

// ==================== 类型定义 ====================

/** MyBatis-Plus IPage 分页结果（后端返回格式） */
interface IPage<T> {
  records: T[];
  total: number;
  size: number;
  current: number;
  pages: number;
}

/** 客服状态枚举 */
export type CsServiceStatus = 1 | 2 | 3;

/** 客服个人信息 */
export interface CsStaffInfo {
  sysUserId: number;
  nickname: string;
  avatar: string;
  serviceStatus: CsServiceStatus;
  currentSessionNum: number;
}

/** 待接入会话 */
export interface PendingChat {
  humanChatId: string;
  userNickname: string;
  userAvatar: string;
  lastMessage: string;
  waitSeconds: number;
  waitStartTime: number;
}

/** 进行中会话 */
export interface ActiveChat {
  humanChatId: string;
  userNickname: string;
  userAvatar: string;
  lastMessage: string;
  lastMsgTime: number;
  unreadCount: number;
}

/** 已结束会话 */
export interface EndedChat {
  humanChatId: string;
  userId: string;
  userNickname: string;
  userAvatar: string;
  endTime: number;
  endType: number;
  durationSeconds: number;
  lastMessage: string;
}

/** 数量统计 */
export interface CsCount {
  myPending: number;
  myActive: number;
  myEnded: number;
  unassigned: number;
}

/** 聊天消息记录 */
export interface ChatLogItem {
  id: string;
  chatId: string;
  role: 'user' | 'assistant' | 'staff' | 'system';
  sysUserId?: number | null;
  staffNickname?: string | null;
  staffAvatar?: string | null;
  userNickname?: string | null;
  userAvatar?: string | null;
  isCurrentStaff: boolean;
  contentType: 'text' | 'multipart';
  content?: string | null;
  parts?: string | null;
  createTime: number;
  /** 前端展示类型：normal普通消息、split分割线、system_tip系统提示消息 */
  displayType?: 'normal' | 'split' | 'system_tip';
  /** 消息发送状态（仅客服发送的消息使用） */
  sendStatus?: 'sending' | 'failed' | 'sent';
}

/** 历史消息返回（IPage格式） */
export interface ChatHistoryRes {
  records: ChatLogItem[];
  total: number;
  size: number;
  current: number;
  pages: number;
}

/** 发送消息请求 */
export interface CsSendMsgReq {
  humanChatId: string;
  contentType: 'text' | 'multipart';
  content?: string | null;
  parts?: string | null;
}

/** 接入会话请求 */
export interface CsAcceptReq {
  humanChatId: string;
}

/** 文件上传响应数据 */
export interface FileUploadData {
  url: string;
  suffix?: string;
  md5?: string;
}

/** 已读标记返回 */
export interface CsReadRes {
  success: boolean;
  unreadCount: number;
  lastReadTime?: number | null;
}

// ==================== 接口常量 ====================

const UPDATE_STATUS_API = `${API_BASE_URL}/ai/cs-api/v1/staff/update-status`;
const STAFF_INFO_API = `${API_BASE_URL}/ai/cs-api/v1/staff/info`;
const PENDING_LIST_API = `${API_BASE_URL}/ai/cs-api/v1/staff/chat/pending/list`;
const ACTIVE_LIST_API = `${API_BASE_URL}/ai/cs-api/v1/staff/chat/active/list`;
const ENDED_LIST_API = `${API_BASE_URL}/ai/cs-api/v1/staff/chat/ended/list`;
const UNASSIGNED_LIST_API = `${API_BASE_URL}/ai/cs-api/v1/staff/chat/unassigned/list`;
const COUNT_API = `${API_BASE_URL}/ai/cs-api/v1/staff/chat/count`;
const ACCEPT_API = `${API_BASE_URL}/ai/cs-api/v1/staff/chat/accept`;
const SEND_API = `${API_BASE_URL}/ai/cs-api/v1/staff/chat/send`;
const CLOSE_API = `${API_BASE_URL}/ai/cs-api/v1/staff/chat/close`;
const HISTORY_API = `${API_BASE_URL}/ai/cs-api/v1/staff/chat/history`;
const FILE_UPLOAD_API_URL = `${API_BASE_URL}/service/app-api/v1/files`;
const CHAT_READ_API = `${API_BASE_URL}/ai/cs-api/v1/staff/chat/read`;

// ==================== 接口函数 ====================

/** 修改客服状态（离线→在线触发第二次分配） */
export function updateCsStatusApi(serviceStatus: CsServiceStatus) {
  return csHttp.put<boolean>(UPDATE_STATUS_API, { serviceStatus });
}

/** 获取自身状态+负载 */
export function getCsStaffInfoApi() {
  return csHttp.get<CsStaffInfo>(STAFF_INFO_API);
}

/** 我的待接入列表 */
export function getPendingListApi(page: number = 1, size: number = 10) {
  return csHttp.get<IPage<PendingChat>>(PENDING_LIST_API, { params: { page, size } });
}

/** 我的进行中列表 */
export function getActiveListApi(page: number = 1, size: number = 10) {
  return csHttp.get<IPage<ActiveChat>>(ACTIVE_LIST_API, { params: { page, size } });
}

/** 我的已结束列表 */
export function getEndedListApi(page: number = 1, size: number = 10) {
  return csHttp.get<IPage<EndedChat>>(ENDED_LIST_API, { params: { page, size } });
}

/** 未分配会话列表 */
export function getUnassignedListApi(page: number = 1, size: number = 10) {
  return csHttp.get<IPage<PendingChat>>(UNASSIGNED_LIST_API, { params: { page, size } });
}

/** 数量统计 */
export function getCsCountApi() {
  return csHttp.get<CsCount>(COUNT_API);
}

/** 接入会话 */
export function acceptChatApi(data: CsAcceptReq) {
  return csHttp.post<boolean>(ACCEPT_API, data);
}

/** 发送消息给用户 */
export function sendCsMsgApi(data: CsSendMsgReq) {
  return csHttp.post<{ chatLogId: string }>(SEND_API, data);
}

/** 客服关闭会话 */
export function closeChatApi(data: CsAcceptReq) {
  return csHttp.post<boolean>(CLOSE_API, data);
}

/** 会话完整历史 */
export function getChatHistoryApi(humanChatId: string, page: number = 1, size: number = 50) {
  return csHttp.get<ChatHistoryRes>(`${HISTORY_API}/${humanChatId}`, { params: { page, size } });
}

/**
 * 客服端文件上传到 S3，返回 URL
 */
export async function uploadCsFile(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await csHttp.post<FileUploadData>(FILE_UPLOAD_API_URL, formData, {
    headers: { 'Content-Type': undefined },
  });

  return response?.url || '';
}

/** 批量标记已读 */
export function markReadApi(data: CsAcceptReq) {
  return csHttp.post<CsReadRes>(CHAT_READ_API, data);
}
